/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: EslUsersPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the ESL Users module.
----------------------------------------------------------------*/

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Edit, Trash2, Shield, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { EslUser } from './types';
import { useEslUserList } from './hooks';
import { deleteEslUser } from './api';
import { navigateToEslUserDuplicate } from './eslUsersActions';

export default function EslUsersPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; eslUser: EslUser | null }>({
    isOpen: false,
    eslUser: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const availableColumns = useMemo(() => [
    {
      key: 'username',
      label: t('eslUsers.columns.username'),
      visible: true,
      sortable: true,
      render: (_value: unknown, item: EslUser) => (
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-foreground">{item?.username ?? ''}</span>
        </div>
      )
    },
    {
      key: 'accessMode',
      label: t('eslUsers.columns.accessMode'),
      visible: true,
      sortable: true,
      render: (_value: unknown, item: EslUser) => (
        <span className="capitalize text-sm text-muted-foreground">
          {t(`eslUsers.accessModes.${item?.accessMode ?? 'inbound'}`)}
        </span>
      )
    },
    {
      key: 'allowedIp',
      label: t('eslUsers.columns.allowedIp'),
      visible: true,
      sortable: true,
      render: (_value: unknown, item: EslUser) => (
        <span className="font-mono text-sm text-muted-foreground">{item?.allowedIp ?? ''}</span>
      )
    },
    {
      key: 'enabled',
      label: t('eslUsers.columns.enabled'),
      visible: true,
      sortable: true,
      render: (_value: unknown, item: EslUser) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}
        >
          {item?.enabled ? t('common.enabled') : t('common.disabled')}
        </span>
      )
    },
    {
      key: 'updatedAt',
      label: t('eslUsers.columns.updatedAt'),
      visible: true,
      sortable: true,
      render: (_value: unknown, item: EslUser) => (
        <span className="text-sm text-muted-foreground">
          {item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : '�'}
        </span>
      )
    },
    {
      key: 'actions',
      label: t('common.actions'),
      visible: true,
      sortable: false
    }
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useEslUserList({ initialColumns: availableColumns });

  const handleCreate = useCallback(() => {
    navigate('/settings/pbx/esl-users/new');
  }, [navigate]);

  const handleEdit = useCallback((item: EslUser) => {
    navigate(`/settings/pbx/esl-users/${item.uuid ?? item.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((item: EslUser) => {
    navigateToEslUserDuplicate(navigate, item.uuid ?? item.id);
  }, [navigate]);

  const handleDeleteClick = useCallback((item: EslUser) => {
    setDeleteModal({ isOpen: true, eslUser: item });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.eslUser) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteEslUser(deleteModal.eslUser.id);

      showSuccess(t('eslUsers.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, eslUser: null });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('eslUsers.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.eslUser, refresh, showError, showSuccess, t]);

  const renderRowActions = useCallback((item: EslUser) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(item)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-md transition-colors"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(item)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-md transition-colors"
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => !item.isDefault && handleDeleteClick(item)}
          disabled={item.isDefault}
          className={`p-2 rounded-md transition-colors ${
            item.isDefault
              ? 'text-muted-foreground/30 cursor-not-allowed opacity-50 dark:text-muted-foreground/30'
              : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
          }`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleEdit, handleDuplicate, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onColumnToggle={handleColumnToggle}
          title={t('eslUsers.title')}
          description={t('eslUsers.description')}
          titleIcon={<Users className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          renderActions={renderRowActions}
          emptyMessage={t('eslUsers.messages.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, eslUser: null })}
        onConfirm={handleDeleteConfirm}
        title={t('common.deleteItem')}
        message={t('eslUsers.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
